import { Vector3 } from "three";
import CanvasInputListenerModeEnum from "../enums/CanvasInputListenerModeEnum";
import CommandIdEnum from "../enums/CommandIdEnums";
import RaycastIntersection from "../logic/RaycastIntersection";
import ICanvasInputListener from "../logic/ICanvasInputListener";
import Document from "../model/document/Document";
import CommandStack from "../logic/CommandStack";
import EditHandleData from "../model/sceneObjectData/EditHandleData";
import SceneObjectData from "../model/sceneObjectData/SceneObjectData";
import ISceneObject from "../model/sceneObject/ISceneObject";
import GeometryEditCmd from "./GeometryEditCmd";
import { SceneObjectStyleEnum, getSceneObjectStyle } from "../model/sceneObject/SceneObjectStyle";
import { emitter } from "../input/AppEvents";
import { useCurrentCommandState } from "../store/currentCommandStateStore";
import { dragMeasurementText } from "../utils/dragMeasurement";

export default class GeometryMoveEditCmd extends GeometryEditCmd {
    #originalData: SceneObjectData | undefined;
    #previewObj: ISceneObject | undefined;
    #committed = false;

    constructor(
        commandStack: CommandStack,
        canvasInputListener: ICanvasInputListener,
        document: Document,
    ) {
        super(CommandIdEnum.GeometryMoveEdit, commandStack, canvasInputListener, document);
        this.commandNameResourceKey = 'command.geometryMoveEdit.title';
        this.initialPrompResourceKey = 'command.geometryMoveEdit.prompt';
    }

    override setParameters(editHandleData: EditHandleData): void {
        super.setParameters(editHandleData);
        this.#originalData = this.ownerSceneObject.data.clone();
    }

    protected setupRaycastTarget(): void {
        if (this.editHandleData.plane) {
            this.canvasInputListener.setTargetPlane(this.editHandleData.plane);
        } else {
            this.canvasInputListener.setTargetPlaneFromCamera(this.editHandleData.position);
        }
        this.canvasInputListener.setRaycastMode(CanvasInputListenerModeEnum.RAYCAST_PLANE);
    }

    activate() {
        super.activate();
        this.setReferencePoint(this.editHandleData.position);
    }

    deactivate() {
        this.disableReferenceLine();
        super.deactivate();
    }

    start() {
        super.start();
        this.addToStack();
        this.connectMouse();

        this.setupRaycastTarget();
        this.activate();

        // Clone the owner into a preview drawable and hide the original
        this.#previewObj = this.ownerSceneObject.clone(true);
        this.#previewObj.geometry.addToScene();
        this.#previewObj.geometry.setStyle(getSceneObjectStyle(SceneObjectStyleEnum.Editing));
        this.#previewObj.onAnnotationDirectionChange(this.modelRotationOffset);
        this.ownerSceneObject.geometry.hide();
    }

    protected getSnapTargetPoint(point: Vector3): Vector3 {
        return this.getRefLineAlignedPoint(point);
    }

    pointerMove(raycastResult: RaycastIntersection | undefined): boolean {
        if (!raycastResult?.point || !this.#previewObj || !this.#originalData) return false;
        const targetPoint = this.getSnapTargetPoint(raycastResult.point);
        const localDelta = this.computeLocalDelta(targetPoint);
        const previewData = this.#originalData.clone();
        this.editHandleData.applyDelta?.(previewData, localDelta);
        this.enrichPreviewData(previewData, raycastResult);

        // Read after enrichPreviewData so any subclass clamping is reflected in the number shown.
        // Handles with nothing meaningful to report write nothing, leaving those drags silent as
        // before — this path never calls startMeasure(), so measureText simply stays null.
        const measureText = dragMeasurementText(previewData.getDragMeasurement(this.editHandleData.handleType));
        if (measureText) useCurrentCommandState.getState().setMeasureText(measureText);

        this.#previewObj.data = previewData;
        this.#previewObj.geometry.update(previewData, true);
        return true;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected enrichPreviewData(_previewData: SceneObjectData, _raycastResult: RaycastIntersection): void {
        // No-op — override in subclasses to post-process the cloned preview data.
    }

    point(raycastResult: RaycastIntersection): boolean {
        if (!this.#previewObj) return true;

        // Promote the preview's final data to the actual scene object
        this.ownerSceneObject.data = this.#previewObj.data.clone();
        this.ownerSceneObject.geometry.update(this.ownerSceneObject.data, true);
        this.ownerSceneObject.geometry.show();

        this.updateEntity(this.ownerSceneObject).then(success => {
            if (!success) console.error('GeometryMoveEditCmd: failed to persist update');
        });

        this.#committed = true;
        emitter.emit('editCommandCommit', { ownerUuid: this.editHandleData.ownerUuid });
        this.end();
        return true;
    }

    cancel(): void {
        this.end();
    }

    end() {
        this.deactivate();
        if (this.#previewObj) {
            this.#previewObj.geometry.removeFromScene();
            this.#previewObj.dispose();
            this.#previewObj = undefined;
        }
        if (!this.#committed) {
            // Cancel — show original (data was never modified)
            this.ownerSceneObject.geometry.show();
            emitter.emit('editCommandEnd', { ownerUuid: this.editHandleData.ownerUuid });
        }
        // Restore target-objects mode for SelectCmd
        this.canvasInputListener.setRaycastMode(CanvasInputListenerModeEnum.RAYCAST_TARGET_OBJECTS);
        super.end();
        this.removeFromStack();
    }
}

